/**
 * Single source of truth for embedded-sidebar mode (?embedded-sidebar=true).
 *
 * Evaluated once at module load — before client-side routing can strip the
 * query param (e.g. navigation to /thread/<id>). All components should import
 * `isEmbeddedSidebar` from this module instead of reading the URL directly.
 */
export const isEmbeddedSidebar: boolean =
    new URLSearchParams(window.location.search).get('embedded-sidebar') === 'true';

// The BIG report author decides PER REPORT whether the embedded chat has report
// tools (a report companion — the default) or is a plain chat with no report tools
// that behaves exactly like the standalone AI tab. The host encodes the choice in
// the iframe URL as `?report-tools=off`; anything else (including absent) means
// report tools are enabled. Read once at module load — same as isEmbeddedSidebar —
// so it survives client-side navigation that strips the query string.
export const embeddedReportToolsEnabled: boolean =
    new URLSearchParams(window.location.search).get('report-tools') !== 'off';

// True only when the chat should act as a report companion (embedded AND report
// tools enabled). Components branch on this for report-companion BEHAVIOUR (hiding
// suggested prompts / voice, showing the Full-report-access toggle). In embedded
// plain chat it is false, so the chat shows exactly what the standalone AI tab
// shows. LAYOUT/cosmetic adaptation stays keyed on isEmbeddedSidebar (plain chat is
// still physically in the narrow sidebar).
export const isReportMode: boolean =
    isEmbeddedSidebar && embeddedReportToolsEnabled;

// The MCP servers attached to the report BY THE REPORT AUTHOR (BIG report
// settings → iframe `?mcp-servers=<a,b,c>`). In an embedded chat this is the ONLY
// way MCP can be enabled — the user cannot pick or connect MCP servers there.
// Absent/empty => no MCP for the embedded chat. `?mcp-server=<name>` (singular) is
// still read as a fallback so an older BIG host keeps working against this build.
// Read once at module load, same as the flags above.
export const embeddedMcpServers: string[] = (() => {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('mcp-servers') ?? params.get('mcp-server') ?? '';
    return raw
        .split(',')
        .map((name) => name.trim())
        .filter(Boolean);
})();

// Whether MCP tools are available in the embedded chat: only when the report
// author attached at least one MCP server to the report.
export const embeddedMcpEnabled: boolean = embeddedMcpServers.length > 0;

// The model the report is pinned to BY THE REPORT AUTHOR (BIG report settings →
// iframe `?model=<catalog model name>`). Absent => the chat falls back to the
// organization's normal agent, which is what every embedded chat did before this
// setting existed. Read once at module load, same as the flags above.
export const embeddedModel: string | null = (() => {
    const value = new URLSearchParams(window.location.search).get('model');
    return value && value.trim() ? value.trim() : null;
})();

// The model picker is shown ONLY in the standalone chat. In an embedded chat the
// model is decided by the report settings, so the user never picks one — the same
// rule the MCP UI follows, and it holds for plain chat too: both embedded shapes
// are configured by the report author, not by whoever is reading the report.
export const modelSelectorAvailable: boolean = !isEmbeddedSidebar;

// MCP UI (sources picker, connect-MCP button) is shown ONLY in the standalone
// chat. In an embedded chat the MCP attachment is fixed by the report settings,
// so the user never selects MCP servers there.
export const mcpToolsAvailable: boolean = !isEmbeddedSidebar;

// True når appen kører inde i en iframe (host embedder chatten).
export const isInIframe: boolean =
    typeof window !== 'undefined' && window.parent !== window;

// Force light theme in embedded mode so the iframe always renders with a
// light background matching the host app.
if (isEmbeddedSidebar) {
    localStorage.setItem('vite-ui-theme', 'light');
}
